import React, {useEffect, useState, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import searchIcon from '../../assets/search-glass.png';
import PageBase from "../../components/PageBase";
import Datatable from "../../components/Datatable";
import * as coSellOpportunityActions from "../../redux/CoSellOpportunity/actions";
import Filters from "../../components/Filters";
import availableFilters from './availableFilters.js';
import LoadingPanel from "../../components/LoadingPanel";
import useColumnFilters from '../../hooks/useColumnFilters.js';
import usePersistentFilters from '../../hooks/usePersistentFilters';
import { getDisplayFields, getDefaultFields } from '../../utils/fields/coSellOpportunities.js';
import ListManager from '../../components/ListManager';
import constants from '../../utils/constants.js';
import {createColumnConfigs} from '../../utils/fieldUtils.js';
import './CoSellOpportunities.scss';

const CoSellOpportunities = () => {
	const [loading, setLoading] = useState(true);
	const [filterResetKey, setFilterResetKey] = useState(0);
	const [listColumns, setListColumns] = useState([]);
	const entityType = `${constants.DISPLAY_ENTITIES.CO_SELL_OPPORTUNITIES}`;

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const coSellOpportunities = useSelector(state => state.coSellOpportunity.coSellOpportunities);
	const total = useSelector(state => state.coSellOpportunity.total);
	const user = useSelector(state => state.user.user);
	const currentAccountName = user?.accountName;

	const {
		search,
		setSearch,
		selectedFilters,
		setSelectedFilters,
		clearFilters: clearPersistentFilters
	} = usePersistentFilters('coSellOpportunities', '', []);

	const fieldsToUse = useMemo(() => {
		return createColumnConfigs(getDisplayFields(listColumns.length === 0, listColumns))
	}, [listColumns]);

	const searchedData = useMemo(() => {
		if (!coSellOpportunities) return [];
		if (!search) return coSellOpportunities;
		const lowerSearch = search.toLowerCase();
		return coSellOpportunities.filter(f => f.Name.toLowerCase().includes(lowerSearch));
	}, [coSellOpportunities, search]);

	const {
		filters,
		sortBy,
		sortOrder,
		filteredData,
		handleFilterChange,
		updateData,
		uniqueValuesByColumn,
		filterOrder
	} = useColumnFilters({
		data: loading ? [] : searchedData,
		columns: fieldsToUse,
		storageKey: `coSellOpportunities`,
		initialState: {}
	});

	useEffect(() => {
		if(selectedFilters.length > 0) {
			return;
		}
		const load = async () => {
			if (currentAccountName) {
				await dispatch(coSellOpportunityActions.loadCoSellOpportunities(currentAccountName, []));
				setLoading(false);
			}
		};
		load();
	}, [currentAccountName]);

	useEffect(() => {
		updateData(coSellOpportunities);
	}, [coSellOpportunities]);

	useEffect(() => {
		if (selectedFilters.length === 0) return;
		const load = async () => {
			setLoading(true);
			await dispatch(coSellOpportunityActions.loadCoSellOpportunities(currentAccountName, selectedFilters));
			setLoading(false);
		};
		load();
	}, [selectedFilters, currentAccountName]);

	if(loading && coSellOpportunities == null) {
		return (
			<PageBase title="Co-Sell Opportunities" removeMargins>
				<LoadingPanel />
			</PageBase>
		)
	}

	const onRowClick = async (row, index) => {
		navigate(`/co-sell-opportunity/${row.Id}`);
	};

	const updateFilter = (value) => {
		setSearch(value.toLowerCase());
	};

	const addFilter = filter => {
		const index = selectedFilters.findIndex(f => f.name === filter.name);

		if(index < 0) {
			setSelectedFilters([...selectedFilters, filter]);
		}
		else {
			const updated = [...selectedFilters];
			updated[index] = filter;
			setSelectedFilters(updated);
		}
	};

	const forceReload = async () => {
		dispatch(coSellOpportunityActions.loadCoSellOpportunities(currentAccountName, selectedFilters, true));
	};

	const removeFilter = (filterToRemove) => {
		const filters = selectedFilters.filter(f => f.name !== filterToRemove.name);
		setSelectedFilters(filters);
	};

	const onClearFilters = () => {
		clearPersistentFilters();
	};

	return (
		<PageBase title="Co-Sell Opportunities" removeMargins>
			<div className="co-sell-opps-header">
				<div className="co-sell-opps-search-box">
					<img src={searchIcon} style={{padding: '0 10px'}} />
					<input className="co-sell-opps-search-input" type="text" value={search} onChange={e => updateFilter(e.target.value)} placeholder="Search Co-Sell Opportunities..." />
				</div>
				<Filters availableFilters={availableFilters()} clearFilters={onClearFilters} selectedFilters={selectedFilters} onAddFilter={addFilter} onRemoveFilter={removeFilter} />

				<div className="co-sell-opps-header-buttons">
					<ListManager
						entityType={entityType}
						onColumnsChange={setListColumns}
						displayFields={getDisplayFields()}
						defaultFields={getDisplayFields(true).map(f => f.field)}
					/>
				</div>
			</div>
			
			{selectedFilters.length > 0 && (
				<div style={{margin: '0 30px'}}>
					<p style={{ color: '#062229', fontWeight: 700, fontSize: 14 }}>Found {filteredData?.length || 0} Opportunities</p>
				</div>
			)}

			<div style={{margin: '0 30px'}}>
				<Datatable 
					gridHeight="calc(100vh - 185px)"
					columns={fieldsToUse}
					data={filteredData}
					key={filterResetKey}
					sortBy={sortBy}
					sortOrder={sortOrder}
					onRowClick={onRowClick}
					filters={filters}
					onFilterChange={handleFilterChange}
					filterOrder={filterOrder}
					uniqueValuesByColumn={uniqueValuesByColumn}
					allData={coSellOpportunities}
				/>
			</div>
		</PageBase>
	)
};

export default CoSellOpportunities; 